import { queryOptions } from '@tanstack/react-query';

import type { AccessToken } from '@/lib/tokenStorage';

import type {
  EquipmentResponse,
  StrippingFertilizationResponse,
  UpdateFertilizationRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchStrippingFertilizations(
  token: AccessToken,
  {
    fertilizationDate,
    siteId,
  }: {
    fertilizationDate: string;
    siteId: string;
  },
): Promise<StrippingFertilizationResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', fertilizationDate);
  searchParams.set('siteId', siteId);

  const url = `${getBaseUrl()}/api/v1/stripping/fertilizations?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as StrippingFertilizationResponse[];
}

async function fetchStrippingEquipments(
  token: AccessToken,
  {
    siteId,
  }: {
    siteId?: string;
  } = {},
): Promise<EquipmentResponse[]> {
  const searchParams = new URLSearchParams();
  if (siteId) {
    searchParams.set('siteId', siteId);
  }

  const query = searchParams.toString();
  const url = `${getBaseUrl()}/api/v1/stripping/equipments${query ? `?${query}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as EquipmentResponse[];
}

export const strippingFertilizationsOptions = (
  token: AccessToken,
  {
    fertilizationDate,
    siteId,
  }: {
    fertilizationDate: string;
    siteId: string;
  },
) =>
  queryOptions({
    queryKey: [
      'stripping',
      'fertilizations',
      'list',
      { fertilizationDate, siteId },
    ],
    enabled: !!token,
    async queryFn() {
      return fetchStrippingFertilizations(token, {
        fertilizationDate,
        siteId,
      });
    },
  });

export const strippingEquipmentsOptions = (
  token: AccessToken,
  {
    siteId,
  }: {
    siteId?: string;
  } = {},
) =>
  queryOptions({
    queryKey: ['stripping', 'equipments', 'list', { siteId }],
    enabled: !!token,
    async queryFn() {
      return fetchStrippingEquipments(token, { siteId });
    },
  });

export async function updateFertilization(
  token: AccessToken,
  id: string,
  input: UpdateFertilizationRequest,
): Promise<void> {
  const url = `${getBaseUrl()}/api/v1/stripping/fertilizations/${id}`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}
